import * as React from "react"
import { Icon } from "./Icon"
import type { AddressLookupOption } from "../types"

const desktopPickerQuery = "(min-width: 980px) and (hover: hover) and (pointer: fine)"

type Props = {
    options: AddressLookupOption[]
    value: string
    onChange: (id: string) => void
    loading: boolean
    disabled: boolean
    hint?: string
    error?: string
    attempt: number
}

export function AddressSelect({ options, value, onChange, loading, disabled, hint, error, attempt }: Props) {
    const id = React.useId()
    const labelId = `${id}-label`
    const listId = `${id}-options`
    const hintId = hint ? `${id}-hint` : undefined
    const errorId = error ? `${id}-error` : undefined
    const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined
    const [desktop, setDesktop] = React.useState(() => typeof window !== "undefined" && window.matchMedia(desktopPickerQuery).matches)
    const [open, setOpen] = React.useState(false)
    const [activeIndex, setActiveIndex] = React.useState(0)
    const [menuLayout, setMenuLayout] = React.useState({ above: false, maxHeight: 320 })
    const rootRef = React.useRef<HTMLDivElement>(null)
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const listRef = React.useRef<HTMLDivElement>(null)
    const search = React.useRef({ text: "", time: 0 })
    const selectedIndex = options.findIndex(option => option.id === value)
    const selected = options[selectedIndex]
    const unavailable = disabled || loading || options.length === 0
    const expanded = desktop && open && !unavailable
    const placeholder = loading ? "Finding your address…" : "Select your delivery address"

    React.useEffect(() => {
        const media = window.matchMedia(desktopPickerQuery)
        const update = () => { setDesktop(media.matches); setOpen(false) }
        media.addEventListener("change", update)
        return () => media.removeEventListener("change", update)
    }, [])

    React.useEffect(() => { setOpen(false) }, [disabled, loading, options])

    React.useLayoutEffect(() => {
        if (!expanded) return
        const positionMenu = () => {
            const bounds = buttonRef.current?.getBoundingClientRect()
            if (!bounds) return
            const below = window.innerHeight - bounds.bottom - 12
            const above = bounds.top - 12
            const flip = below < Math.min(320, options.length * 68 + 12) && above > below
            const maxHeight = Math.max(80, Math.min(320, flip ? above : below))
            setMenuLayout(current => current.above === flip && current.maxHeight === maxHeight ? current : { above: flip, maxHeight })
        }
        const closeOutside = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
        }
        positionMenu()
        document.addEventListener("pointerdown", closeOutside)
        window.addEventListener("resize", positionMenu)
        window.addEventListener("scroll", positionMenu, true)
        return () => {
            document.removeEventListener("pointerdown", closeOutside)
            window.removeEventListener("resize", positionMenu)
            window.removeEventListener("scroll", positionMenu, true)
        }
    }, [expanded, options.length])

    React.useLayoutEffect(() => {
        const list = listRef.current
        const option = list?.children[activeIndex] as HTMLElement | undefined
        if (!expanded || !list || !option) return
        // Scroll only the options, keeping the checkout itself in place.
        if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop
        else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight
        }
    }, [expanded, activeIndex, menuLayout.maxHeight])

    const show = (index = Math.max(0, selectedIndex)) => {
        if (unavailable) return
        search.current = { text: "", time: 0 }
        setActiveIndex(index)
        setOpen(true)
    }
    const choose = (index: number) => {
        if (unavailable || !options[index]) return
        onChange(options[index].id)
        search.current = { text: "", time: 0 }
        setOpen(false)
    }
    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (unavailable) return
        const { key } = event
        if (key === "Tab") {
            if (expanded) choose(activeIndex)
            return
        }
        if (key === "Escape") {
            if (expanded) { event.preventDefault(); event.stopPropagation(); setOpen(false) }
            return
        }
        if (key === "Enter" || (key === " " && (!search.current.text || Date.now() - search.current.time >= 700))) {
            event.preventDefault()
            if (expanded) choose(activeIndex)
            else show()
            return
        }
        if (["ArrowDown", "ArrowUp", "Home", "End", "PageDown", "PageUp"].includes(key)) {
            event.preventDefault()
            search.current = { text: "", time: 0 }
            if (event.altKey && key === "ArrowUp" && expanded) { choose(activeIndex); return }
            const current = expanded ? activeIndex : Math.max(0, selectedIndex)
            const next = key === "Home" ? 0 : key === "End" ? options.length - 1
                : !expanded ? current
                : current + (key === "ArrowDown" ? 1 : key === "ArrowUp" ? -1 : key === "PageDown" ? 10 : -10)
            setActiveIndex(Math.max(0, Math.min(options.length - 1, next)))
            setOpen(true)
            return
        }
        if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault()
            const now = Date.now()
            const text = (now - search.current.time < 700 ? search.current.text : "") + key.toLocaleLowerCase()
            search.current = { text, time: now }
            const repeated = [...text].every(character => character === text[0])
            const query = repeated ? text[0] : text
            const start = repeated && expanded ? activeIndex + 1 : 0
            const indices = options.map((_, index) => (index + start) % options.length)
            const match = indices.find(index => options[index].label.toLocaleLowerCase().startsWith(query))
            if (match !== undefined) setActiveIndex(match)
            else if (!expanded) setActiveIndex(Math.max(0, selectedIndex))
            setOpen(true)
        }
    }

    const common = {
        id,
        "data-detail-field": "address",
        "aria-labelledby": labelId,
        "aria-describedby": describedBy,
        "aria-invalid": Boolean(error),
        "aria-busy": loading,
        disabled: unavailable,
    }
    return <div className={`ah-field ah-detail-field ah-address-field${error ? " is-invalid" : ""}`} data-shake={attempt % 2}>
        <label id={labelId} htmlFor={id} className="ah-visually-hidden">Select your delivery address</label>
        <div className={`ah-address-picker${expanded ? " is-open" : ""}`} ref={rootRef}>
            {desktop ? <button {...common} ref={buttonRef} type="button" role="combobox" aria-haspopup="listbox" aria-controls={expanded ? listId : undefined} aria-expanded={expanded} aria-activedescendant={expanded ? `${id}-option-${activeIndex}` : undefined}
                className={`ah-address-trigger${selected ? "" : " is-placeholder"}`}
                onClick={() => expanded ? setOpen(false) : show()} onKeyDown={onKeyDown} onBlur={() => setOpen(false)}>
                <span>{selected?.label || placeholder}</span>
            </button> : <select {...common} className={`ah-address-select${selected ? "" : " is-placeholder"}`} value={value} onChange={event => onChange(event.target.value)}>
                <option value="">{placeholder}</option>
                {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>}
            <svg className="ah-address-chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>
            {expanded && <div ref={listRef} id={listId} role="listbox" aria-labelledby={labelId} className={`ah-address-menu${menuLayout.above ? " is-above" : ""}`} style={{ maxHeight: menuLayout.maxHeight }}>
                {options.map((option, index) => <div key={option.id} id={`${id}-option-${index}`} role="option" aria-selected={option.id === value} data-address-id={option.id} data-active={index === activeIndex} className="ah-address-option" onPointerDown={event => event.preventDefault()} onPointerMove={() => setActiveIndex(index)} onClick={() => choose(index)}>
                    <span><strong>{option.line1}</strong><small>{[option.line2, option.city, option.postalCode].filter(Boolean).join(", ")}</small></span>
                    {option.id === value && <Icon name="check" />}
                </div>)}
            </div>}
        </div>
        {hint && <small id={hintId}>{hint}</small>}
        {error && <small id={errorId} className="ah-field-error" aria-live="polite">{error}</small>}
    </div>
}
